import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Town } from 'types/types'
import { LETTERS_NUMBERS_SLASHES_DASHES, POSTAL_CODE } from 'constants/regex'
import request, { getAxiosRequestConfig } from 'utils/request'
import { Methods } from 'types/request'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { useGetLanguage } from 'hooks/use-get-language'
import { Banner, Button, Form, Heading, InlineMessage, Select, TextField, popSuccessToast, ToggleRadio } from '@boltenergy-be/design-system'
import { ContractResponseCodes } from 'types/errorCodes.ts'
import styles from './ContactForm.module.scss'
import { ContactFormInputs } from 'pages/app/user/types'
import { updateBillingContract, updateAccount } from 'store/contact/thunks'
import { ThunkDefaultErrorMsg } from 'store/contact/thunks/types.ts'
import { formatAddress } from 'utils/user'
import { routes } from 'types/routes.ts'
import { useNavigate } from 'react-router'
import { selectCurrentAccount, selectCurrentBillingContract, selectCurrentProduct } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import { CONTACT_MODES, PRODUCTS_CONFIG } from '@boltenergy-be/shared'
import { IS_PROD_TESTING } from 'constants/envs.ts'
import { UserTypes } from 'store/auth/types.ts'

const ContactForm = () => {
  // Redux
  const { error, loading } = useStoreSelector((store) => store.contact)
  const { userType } = useStoreSelector((store) => store.auth)
  const billingContract = useStoreSelector(selectCurrentBillingContract)
  const account = useStoreSelector(selectCurrentAccount)
  const currentProduct = useStoreSelector(selectCurrentProduct)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['user', 'common', 'validation', 'errors'])
  const language = useGetLanguage()

  // Router
  const navigate = useNavigate()

  // Local state
  const [saved, setSaved] = useState(false)
  const [updating, setUpdating] = useState<boolean>(false)
  const [towns, setTowns] = useState<Town[]>([])

  // React hook form
  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm<ContactFormInputs>({
    mode: 'onChange',
    defaultValues: {
      contactMode: billingContract.deliveryMode,
      address: account.correspondenceAddress
    }
  })
  const watchTownName = useWatch({ control, name: 'address.townName' })
  const watchContactMode = useWatch({ control, name: 'contactMode' })

  // Constants
  const currentProductConfig = currentProduct ? PRODUCTS_CONFIG[currentProduct] : undefined
  const digitalContactOnly = currentProductConfig ? currentProductConfig?.digitalOnly?.billingOptions : false
  const disabledContactModeToggle = watchContactMode === CONTACT_MODES.email && digitalContactOnly

  /**
   * Fetches the towns based on the current postal code
   * Triggered on first render
   */
  useEffect(() => {
    if (account.correspondenceAddress.postalCode) void fetchTowns(Number(account.correspondenceAddress.postalCode))
  }, [])

  /**
   * Fetches the towns
   *
   * @param postalCode
   */
  const fetchTowns = async (postalCode: number) => {
    if (isValidPostalCode(postalCode)) {
      try {
        clearErrors()

        // Fetch the towns & the region
        const { success, data } = await request(getAxiosRequestConfig(Methods.GET, `towns/${postalCode}`))

        if (success) {
          const { towns } = data
          setTowns(towns)

          const filteredTowns = towns.filter((town: Town) => town.townName === account.correspondenceAddress.townName)
          setValue('address.townName', filteredTowns.length > 0 ? filteredTowns[0].townName : towns[0].townName)
        } else {
          setTowns([])
          setError('address.postalCode', { type: 'validate' })
        }
      } catch (e) {
        setTowns([])
        setError('address.postalCode', { type: 'api' })
      }
    } else {
      setTowns([])
      setError('address.postalCode', { type: 'validate' })
    }
  }

  /**
   * Returns if a given postal code is valid
   *
   * @param postalCode
   * @returns boolean
   */
  const isValidPostalCode = (postalCode: number): boolean => {
    return postalCode.toString().length === 4
  }

  /**
   * Handles the form submission after validation by React Hook Form
   *
   * @param {ContactFormInputs} data
   */
  const onSubmit = async (data: ContactFormInputs) => {
    setUpdating(true)

    const [accountRes, contractRes] = await Promise.all([
      // Update the account (only map the fields that have changed since it's a PATCH request)
      dispatch(
        updateAccount({
          accountId: account.sfId,
          contactId: account.contactSfId,
          account: {
            correspondenceAddress: {
              ...account.correspondenceAddress,
              ...data.address
            },
            deliveryMode: data.contactMode
          }
        })
      ),
      // Update billing contract
      dispatch(
        updateBillingContract({
          billingContractId: billingContract.contractNumber,
          updateBillingContractData: {
            deliveryMode: data.contactMode
          }
        })
      )
    ])

    if (accountRes.meta.requestStatus === 'fulfilled' && contractRes.meta.requestStatus === 'fulfilled') {
      trackEvent({ category: 'user', object: 'edit_contact_info', action: 'submit' })
      void navigate(routes.USER_CONTACT)
      popSuccessToast(
        t(($) => $.formFeedback.saved, {
          ns: 'common'
        })
      )
    }

    setSaved(true)
    setUpdating(false)
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className={styles['contact-form']}>
      {account.billingContracts.length > 1 && (
        <Banner type="informative" title={t(($) => $.contact.banner)}>
          {formatAddress(billingContract.serviceContracts.electricity.deliveryPoint.address)}
        </Banner>
      )}
      <Heading as="h2" variant="h4" className="grid-col-full">
        {t(($) => $.contact.fields.preferences.title)}
      </Heading>

      <ToggleRadio
        grid="full"
        name="contactMode"
        label={t(($) => $.contact.fields.preferences.label)}
        message={
          disabledContactModeToggle && currentProductConfig
            ? {
                type: 'neutral',
                text: t(($) => $.contact.fields.preferences.disabled, {
                  product: currentProductConfig.name[language],
                  context: userType === UserTypes.SUPER_USER ? 'superuser' : 'customer'
                })
              }
            : undefined
        }
        disabled={disabledContactModeToggle}
        options={[
          {
            ...register('contactMode'),
            value: CONTACT_MODES.email,
            id: CONTACT_MODES.email,
            label: t(($) => $.contact.fields.preferences.receiveEmail)
          },
          {
            ...register('contactMode'),
            value: CONTACT_MODES.post,
            id: CONTACT_MODES.post,
            label: t(($) => $.contact.fields.preferences.receivePost)
          }
        ]}
      />

      <Heading as="h2" variant="h4" className="mt-400 grid-col-full">
        {t(($) => $.contact.fields.billing.title)}
      </Heading>

      {/*  STREET */}
      <TextField
        id="address.streetName"
        label={t(($) => $.contact.fields.billing.streetName)}
        {...register('address.streetName', {
          required: t(($) => $.required, {
            ns: 'validation'
          })
        })}
        error={errors?.address?.streetName?.message}
      />

      <div className={styles.subgrid}>
        {/* STREET NUMBER */}
        <TextField
          id="address.streetNumber"
          label={t(($) => $.contact.fields.billing.streetNumber)}
          {...register('address.streetNumber', {
            required: t(($) => $.required, {
              ns: 'validation'
            }),
            maxLength: {
              value: 5,
              message: t(($) => $.invalid.streetNumber, {
                ns: 'validation'
              })
            },
            pattern: {
              value: LETTERS_NUMBERS_SLASHES_DASHES,
              message: t(($) => $.invalid.streetNumber, {
                ns: 'validation'
              })
            }
          })}
          error={errors?.address?.streetNumber?.message}
        />

        {/* STREET BOX */}
        <TextField
          id="address.streetBox"
          label={t(($) => $.contact.fields.billing.streetBox)}
          {...register('address.streetBox')}
          error={errors?.address?.streetBox?.message}
        />
      </div>

      {/* POSTAL CODE */}
      <TextField
        id="address.postalCode"
        label={t(($) => $.contact.fields.billing.postalCode)}
        {...register('address.postalCode', {
          required: t(($) => $.required, {
            ns: 'validation'
          }),
          validate: (val) =>
            isValidPostalCode(Number(val)) ||
            t(($) => $.invalid.postalCode, {
              ns: 'validation'
            }),
          pattern: {
            value: POSTAL_CODE,
            message: t(($) => $.invalid.postalCode, {
              ns: 'validation'
            })
          },
          onChange: (e) => fetchTowns(Number(e.target.value))
        })}
        type="number"
        error={errors?.address?.postalCode?.message}
      />

      {/* TOWN NAME */}
      <Select
        id="address.townName"
        label={t(($) => $.contact.fields.billing.townName)}
        {...register('address.townName', {
          required: t(($) => $.required, {
            ns: 'validation'
          })
        })}
        value={watchTownName}
        error={errors?.address?.townName?.message}
      >
        {towns.map((town) => (
          <option key={town.townCode} value={town.townName}>
            {town.townName}
          </option>
        ))}
      </Select>

      <Button
        isFullwidth
        type="submit"
        disabled={IS_PROD_TESTING || Object.keys(errors).length > 0}
        loading={loading || updating}
        className="grid-col-full"
      >
        {t(($) => $.save, {
          ns: 'common'
        })}
      </Button>

      {saved &&
        error &&
        [
          ThunkDefaultErrorMsg.UPDATE_BILLING_CONTRACT as string,
          ThunkDefaultErrorMsg.UPDATE_ACCOUNT as string,
          ContractResponseCodes.MERCATOR_CUSTOMER_NOT_FOUND
        ].includes(error) && (
          <InlineMessage type="negative">
            {error === ContractResponseCodes.MERCATOR_CUSTOMER_NOT_FOUND
              ? t(($) => $.mercator.notFound, {
                  ns: 'errors'
                })
              : t(($) => $.default, {
                  ns: 'errors'
                })}
          </InlineMessage>
        )}
    </Form>
  )
}

export default ContactForm
